import {useFormik} from 'formik';
import {useState} from 'react';
import * as Yup from 'yup';
import {StoryScreenModel, StoryParagraphModel, StoryMediaType} from '../../../entities';
import {PreparedImageData} from '../../fundraiserImages';
import {useStoryScreensStore} from './useStoryScreensStore';

interface UseAddStoryScreenFormProps {
  storyId: string;
  setOpen: (open: boolean) => void;
}

interface StoryScreenFormData {
  paragraphs: StoryParagraphModel[];
  showButton: boolean;
  buttonText: string;
  buttonUrl: string;
  buttonTheme: 'Default' | 'White' | 'Black';
  theme: 'Light' | 'Dark';
  alignToBottom: boolean;
  mediaType: StoryMediaType;
  imageData: PreparedImageData | null;
  imagePreview: string | null;
  videoUploadId: string | null;
  videoPreview: string | null;
}

export const useAddStoryScreenForm = ({storyId, setOpen}: UseAddStoryScreenFormProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {addScreen} = useStoryScreensStore();

  const onSubmit = async (values: StoryScreenFormData) => {
    setErrorMessage(null);
    try {
      // Фильтруем пустые параграфы перед сохранением
      const filteredParagraphs = values.paragraphs.filter(p => p.text.trim() !== '');

      const newScreen: Omit<StoryScreenModel, 'id'> = {
        mediaType: values.mediaType,
        imageUploadId: values.mediaType === 'image' ? (values.imageData?.uploadId || null) : null,
        videoUploadId: values.mediaType === 'video' ? (values.videoUploadId || null) : null,
        imagePreview: values.mediaType === 'image' ? values.imagePreview : null,
        videoPreview: values.mediaType === 'video' ? values.videoPreview : null,
        paragraphs: filteredParagraphs,
        button: values.showButton
          ? {
              text: values.buttonText,
              link: values.buttonUrl,
              theme: values.buttonTheme,
            }
          : null,
        theme: values.theme,
        alignToBottom: values.alignToBottom,
      };

      addScreen(storyId, newScreen);
      setOpen(false);
    } catch (error: any) {
      setErrorMessage(error?.message || 'Произошла ошибка при создании экрана');
    }
  };

  const formik = useFormik<StoryScreenFormData>({
    initialValues: {
      paragraphs: [
        {style: 'h1', text: ''},
        {style: 'p2', text: ''},
      ],
      showButton: false,
      buttonText: '',
      buttonUrl: '',
      buttonTheme: 'Default',
      theme: 'Light',
      alignToBottom: false,
      mediaType: 'image',
      imageData: null,
      imagePreview: null,
      videoUploadId: null,
      videoPreview: null,
    },
    validationSchema: Yup.object({
      paragraphs: Yup.array()
        .of(
          Yup.object({
            style: Yup.string().required(),
            text: Yup.string(),
          }),
        )
        .test('has-text', 'Необходимо добавить хотя бы один параграф с текстом', function (paragraphs) {
          return paragraphs?.some((p) => (p?.text || '').trim() !== '') || false;
        }),
      buttonText: Yup.string().when('showButton', {
        is: true,
        then: (schema) => schema.required('Введите текст кнопки'),
      }),
      buttonUrl: Yup.string().when('showButton', {
        is: true,
        then: (schema) => schema.required('Введите URL кнопки'),
      }),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit,
  });

  return {
    ...formik,
    isLoading: false,
    submitError: undefined,
    errorMessage,
    submitForm: () => formik.handleSubmit(),
    isValid: getIsFormValid(formik.values),
  };
};

const getIsFormValid = (values: StoryScreenFormData): boolean => {
  const hasTextInParagraphs = values.paragraphs?.some((p) => (p?.text || '').trim() !== '');

  const buttonValid = !values.showButton || (values.buttonText.trim() !== '' && values.buttonUrl.trim() !== '');
  return hasTextInParagraphs && buttonValid;
};
